import React, { useEffect } from 'react';
import { Plus, ArrowLeft, RefreshCw } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { formatAmount } from '../utils/format';
import TransactionItem from '../components/TransactionItem';

const AccountDetail = () => {
  const {
    selectedAccount,
    transactions,
    loading,
    currentPage,
    hasMoreTransactions,
    setScreen,
    setSelectedTransaction,
    setLoading,
    fetchAccountTransactions
  } = useApp();

  const [loadingMore, setLoadingMore] = React.useState(false);

  const loadMoreTransactions = async () => {
    if (loadingMore || !hasMoreTransactions) return;
    setLoadingMore(true);
    const nextPage = currentPage + 1;
    await fetchAccountTransactions(selectedAccount?.id, nextPage, true);
    setLoadingMore(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      if (scrollHeight - scrollTop - clientHeight < 200 && !loadingMore && hasMoreTransactions) {
        loadMoreTransactions();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMoreTransactions, currentPage]);

  const handleTransactionClick = (tx) => {
    setSelectedTransaction(tx);
    setScreen('transactionDetail');
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      await fetchAccountTransactions(selectedAccount?.id);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedAccount) {
    setScreen('dashboard');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 pb-20">
      {/* Header */}
      <div className="bg-indigo-600 text-white p-6 shadow-lg">
        <button
          onClick={() => setScreen('dashboard')}
          className="mb-4 flex items-center text-indigo-100 hover:text-white"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </button>

        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">{selectedAccount.name}</h1>
            {selectedAccount.bank && (
              <p className="text-indigo-200">{selectedAccount.bank}</p>
            )}
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="p-2 hover:bg-white/10 rounded-lg transition"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <p className="text-3xl font-bold mt-4">
          ${formatAmount(parseFloat(selectedAccount?.balance || 0))}
        </p>
      </div>

      {/* Transactions */}
      <div className="p-4">
        <h2 className="text-lg font-bold text-gray-800 mb-3">Transactions</h2>

        {loading && transactions.length === 0 ? (
          <div className="text-center py-8">
            <RefreshCw className="w-8 h-8 mx-auto text-indigo-600 animate-spin mb-2" />
            <p className="text-gray-500">Loading transactions...</p>
          </div>
        ) : transactions.length > 0 ? (
          <div className="space-y-2">
            {transactions.map((tx, idx) => (
              <TransactionItem
                key={tx.id || idx}
                transaction={tx}
                onClick={() => handleTransactionClick(tx)}
              />
            ))}

            {loadingMore && (
              <div className="text-center py-4">
                <RefreshCw className="w-6 h-6 mx-auto text-indigo-600 animate-spin" />
              </div>
            )}

            {!hasMoreTransactions && transactions.length > 0 && (
              <p className="text-center text-gray-400 py-4">No more transactions</p>
            )}
          </div>
        ) : (
          <div className="rounded-xl p-8 text-center">
            <p className="text-gray-400">No transactions found</p>
            <p className="text-sm text-gray-400 mt-2">This account has no recent transactions</p>
          </div>
        )}
      </div>

      {/* FAB */}
      <button
        onClick={() => setScreen('add')}
        className="fixed bottom-6 right-6 bg-indigo-600 text-white p-4 rounded-full shadow-lg hover:bg-indigo-700 transition"
      >
        <Plus className="w-8 h-8" />
      </button>
    </div>
  );
};

export default AccountDetail;
